/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `CustomizeEventSubmenu` for the Caintra Connect frontend.
 * @module components/SideMenu/CustomizeEventSubmenu
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import styles from "../../styles/SideMenu.module.css";
import { ReactComponent as BusinessEventsIcon } from "../../assets/icons/SideMenu/BusinessEventsIcon.svg";
import {
  canManageB2BEvents,
  canManageBreakfastEvents,
  canManageBusinessEvents,
  canManageRegistrationEvents,
  canManageVirtualEvents,
} from "../../utils/accessControl";
import { ROUTES } from "./constants";
import { isCustomizeEventSectionActive } from "./sideMenuUtils";

/** Single submenu entry under "Personalizar evento". */
const SubmenuItem = ({ label, isActive, onClick }) => (
  <div
    className={`${styles.submenuItem} ${isActive ? styles.active : ""}`}
    onClick={onClick}
  >
    <span className={styles.submenuItemText}>{label}</span>
  </div>
);

/**
 * Expandable "Personalizar evento" section with conditional submenu entries.
 * Visibility of each entry depends on user permissions and role.
 */
const CustomizeEventSubmenu = ({
  user,
  permissions,
  location,
  personalizarEventoExpanded,
  setPersonalizarEventoExpanded,
  isMenuItemActive,
  navigateTo,
}) => {
  const { t } = useTranslation();

  const canSeeSection =
    canManageBusinessEvents(user, permissions) ||
    canManageB2BEvents(user, permissions) ||
    canManageVirtualEvents(user, permissions) ||
    user?.compradorActivo ||
    canManageRegistrationEvents(user, permissions) ||
    canManageBreakfastEvents(user, permissions);

  if (!canSeeSection) {
    return null;
  }

  const isParentActive = isCustomizeEventSectionActive(
    location,
    (path, context) => isMenuItemActive(path, context),
  );

  return (
    <div className={styles.menuItemWithSubmenu}>
      <div
        className={`${styles.menuItem} ${isParentActive ? styles.active : ""}`}
        onClick={() => setPersonalizarEventoExpanded((prev) => !prev)}
      >
        <BusinessEventsIcon className={styles.menuIcon} />
        <span className={styles.menuText}>
          {t("pages.menu.customizeEvent")}
        </span>
        <span className={styles.menuItemArrow}>
          {personalizarEventoExpanded ? (
            <KeyboardArrowUp sx={{ fontSize: 20 }} />
          ) : (
            <KeyboardArrowDown sx={{ fontSize: 20 }} />
          )}
        </span>
      </div>

      {personalizarEventoExpanded && (
        <div className={styles.submenu}>
          {canManageRegistrationEvents(user, permissions) && (
            <SubmenuItem
              label={t("pages.menu.events")}
              isActive={isMenuItemActive(ROUTES.MANAGE_EVENTS)}
              onClick={() => navigateTo(ROUTES.MANAGE_EVENTS)}
            />
          )}

          {canManageBusinessEvents(user, permissions) && (
            <SubmenuItem
              label={t("pages.menu.businessEvent")}
              isActive={isMenuItemActive(ROUTES.BUSINESS_EVENTS)}
              onClick={() => navigateTo(ROUTES.BUSINESS_EVENTS)}
            />
          )}

          {canManageB2BEvents(user, permissions) && (
            <SubmenuItem
              label={t("pages.menu.b2bEvents")}
              isActive={isMenuItemActive(ROUTES.B2B_EVENTS)}
              onClick={() => navigateTo(ROUTES.B2B_EVENTS)}
            />
          )}

          {canManageVirtualEvents(user, permissions) && (
            <SubmenuItem
              label={t("pages.menu.virtualEvents")}
              isActive={isMenuItemActive(ROUTES.VIRTUAL_EVENTS)}
              onClick={() => navigateTo(ROUTES.VIRTUAL_EVENTS)}
            />
          )}

          {canManageBreakfastEvents(user, permissions) && (
            <SubmenuItem
              label={t("pages.menu.breakfastEvents")}
              isActive={isMenuItemActive(ROUTES.BREAKFAST_EVENTS)}
              onClick={() => navigateTo(ROUTES.BREAKFAST_EVENTS)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CustomizeEventSubmenu;
